import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Clock, Eye, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface Clip {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  duration: number;
  view_count: number;
  created_at: string;
  creator_id: string;
  profiles: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface ClipsGalleryProps {
  creatorId?: string;
  limit?: number;
}

export const ClipsGallery = ({ creatorId, limit = 12 }: ClipsGalleryProps) => {
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadClips();
  }, [creatorId, limit]);

  const loadClips = async () => {
    try {
      const { data, error } = await supabase
        .from('recordings')
        .select(`
          id,
          title,
          description,
          video_url,
          thumbnail_url,
          duration,
          view_count,
          created_at,
          creator_id
        `)
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(creatorId ? limit : limit);

      if (error) throw error;

      if (!data) {
        setClips([]);
        return;
      }

      // Fetch creator profiles separately
      const creatorIds = [...new Set(data.map(clip => clip.creator_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', creatorIds);

      const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Combine clips with profile data
      const clipsWithProfiles = data
        .filter(clip => creatorId ? clip.creator_id === creatorId : true)
        .map(clip => ({
          ...clip,
          profiles: profilesMap.get(clip.creator_id) || {
            username: 'Unknown',
            display_name: null,
            avatar_url: null,
          }
        }));

      setClips(clipsWithProfiles as Clip[]);
    } catch (error) {
      console.error('Error loading clips:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClipClick = (clipId: string) => {
    // Navigate to clip player or modal
    navigate(`/clip/${clipId}`);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <Skeleton className="aspect-video w-full" />
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center text-muted-foreground">
          <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No clips available yet</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {clips.map((clip) => (
        <Card
          key={clip.id}
          className="group cursor-pointer hover:shadow-lg transition-all overflow-hidden"
          onClick={() => handleClipClick(clip.id)}
        >
          {/* Thumbnail */}
          <div className="relative aspect-video bg-muted overflow-hidden">
            {clip.thumbnail_url ? (
              <img
                src={clip.thumbnail_url}
                alt={clip.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                <Play className="h-16 w-16 text-primary/50" />
              </div>
            )}
            
            {/* Duration Badge */}
            <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(clip.duration)}
            </div>

            {/* Play Overlay */}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                <Play className="h-8 w-8 text-white fill-white" />
              </div>
            </div>
          </div>

          {/* Content */}
          <CardContent className="p-4 space-y-3">
            {/* Title */}
            <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
              {clip.title}
            </h3>

            {/* Creator Info */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {clip.profiles.avatar_url ? (
                <img
                  src={clip.profiles.avatar_url}
                  alt={clip.profiles.username}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-3 w-3 text-primary" />
                </div>
              )}
              <span className="truncate">
                {clip.profiles.display_name || clip.profiles.username}
              </span>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                <span>{clip.view_count.toLocaleString()} views</span>
              </div>
              <span>
                {formatDistanceToNow(new Date(clip.created_at), { addSuffix: true })}
              </span>
            </div>

            {/* Description Preview */}
            {clip.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {clip.description}
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};